import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, Milk, Minus, Plus, ShoppingCart, Truck } from 'lucide-react';
import { cn } from '../utils/cn';
import { mediaUrl } from '../utils/site';
import { cartCount, rupees, setQty, useCart, type ShopProduct } from './shopApi';
import { useShopFront } from './ShopLayout';
import { AwaitingPayment } from './AwaitingPayment';

export function Stepper({ product, qty }: { product: ShopProduct; qty: number }) {
  if (qty === 0) {
    return (
      <button type="button" disabled={!product.in_stock} onClick={() => setQty(product.id, 1)}
        className="w-full rounded-lg bg-brand-green-700 py-2 text-sm font-semibold text-white disabled:bg-ink-300">
        {product.in_stock ? 'Add' : 'Out of stock'}
      </button>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-lg border border-brand-green-600 bg-brand-green-50">
      <button type="button" aria-label="One less" onClick={() => setQty(product.id, qty - 1)} className="px-3 py-2 text-brand-green-800"><Minus className="h-4 w-4" /></button>
      <span className="font-bold text-brand-green-900 tabular-nums" aria-live="polite">{qty}</span>
      <button type="button" aria-label="One more" disabled={qty >= product.max_per_order} onClick={() => setQty(product.id, qty + 1)} className="px-3 py-2 text-brand-green-800 disabled:opacity-30"><Plus className="h-4 w-4" /></button>
    </div>
  );
}

export function ProductPhoto({ product, className }: { product: ShopProduct; className?: string }) {
  return product.photo ? (
    <img src={mediaUrl(product.photo)} alt={product.name} loading="lazy" className={cn('object-cover', className)} />
  ) : (
    <div className={cn('flex items-center justify-center bg-gradient-to-br from-brand-green-50 to-brand-gold-50', className)}>
      <Milk className="h-10 w-10 text-brand-green-600" strokeWidth={1.5} />
    </div>
  );
}

export default function ShopHome() {
  const q = useShopFront();
  const cart = useCart();
  const [cat, setCat] = useState('All');
  const front = q.data;
  const cats = useMemo(() => ['All', ...Array.from(new Set((front?.products ?? []).map((p) => p.category)))], [front]);
  const products = (front?.products ?? []).filter((p) => cat === 'All' || p.category === cat);
  const count = cartCount(cart);
  const total = (front?.products ?? []).reduce((a, p) => a + Number(p.price) * (cart[p.id] ?? 0), 0);

  if (q.isLoading) return <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-60 animate-pulse rounded-xl bg-white" />)}</div>;
  if (q.isError || !front) return <p className="rounded-lg bg-white p-6 text-center text-ink-500">The shop couldn't load. Please check your internet and try again.</p>;

  return (
    <div className="flex flex-col gap-4">
      <AwaitingPayment />
      {!front.open && (
        <div className="rounded-xl border border-warning-500/40 bg-warning-50 p-3 text-sm text-warning-700">{front.paused_message}</div>
      )}
      {front.notice && (
        <div className="flex gap-2 rounded-xl bg-brand-gold-50 p-3 text-sm text-brand-gold-900"><Info className="h-4 w-4 shrink-0 mt-0.5" /> {front.notice}</div>
      )}

      <div className="rounded-2xl bg-gradient-to-br from-brand-green-700 to-brand-green-900 p-4 text-white">
        <p className="text-lg font-bold">Fresh from our farm</p>
        <p className="text-sm text-brand-green-100">Order today, pay by UPI, get it at your door — morning or evening.</p>
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand-green-100">
          <Truck className="h-3.5 w-3.5" />
          {Number(front.delivery_fee) > 0 ? `Delivery ${rupees(front.delivery_fee)}` : 'Free delivery'}
          {Number(front.free_delivery_above) > 0 && ` · free above ${rupees(front.free_delivery_above)}`}
          {Number(front.min_order) > 0 && ` · minimum order ${rupees(front.min_order)}`}
        </p>
      </div>

      {cats.length > 2 && (
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
          {cats.map((c) => (
            <button key={c} type="button" onClick={() => setCat(c)}
              className={cn('shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium', cat === c ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/40 bg-white text-ink-600')}>
              {c}
            </button>
          ))}
        </div>
      )}

      {products.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-center text-ink-500">No products yet — please check back soon.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((p) => (
            <div key={p.id} className="flex flex-col overflow-hidden rounded-xl border border-brand-green-100 bg-white shadow-card">
              <ProductPhoto product={p} className="aspect-square w-full" />
              <div className="flex flex-1 flex-col gap-1 p-3">
                <p className="font-semibold leading-tight text-ink-900">{p.name}</p>
                <p className="text-xs text-ink-400">{p.pack_label}</p>
                {p.description && <p className="line-clamp-2 text-xs text-ink-500">{p.description}</p>}
                <p className="mt-auto pt-1 text-lg font-bold text-brand-green-800 tabular-nums">{rupees(p.price)}</p>
                <Stepper product={p} qty={cart[p.id] ?? 0} />
              </div>
            </div>
          ))}
        </div>
      )}

      {count > 0 && (
        <Link to="/shop/cart" className="sticky bottom-20 z-20 flex items-center justify-between rounded-xl bg-brand-green-800 px-4 py-3 text-white shadow-popover md:bottom-4">
          <span className="inline-flex items-center gap-2 font-semibold"><ShoppingCart className="h-5 w-5" /> {count} item{count > 1 ? 's' : ''} · {rupees(total)}</span>
          <span className="text-sm font-semibold">View cart →</span>
        </Link>
      )}
    </div>
  );
}
